import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart3, LifeBuoy, LogOut, Megaphone } from 'lucide-react';
import { type LucideIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { adminSupportService, ADMIN_SUPPORT_CHANGED_EVENT } from '@/services/adminSupportService';

const ADMIN_SECRET = import.meta.env.VITE_ADMIN_PATH_SECRET ?? '__ops_dev';

interface AdminTab {
  path: string;
  labelKey: string;
  icon: LucideIcon;
  /** When true, this tab renders the cross-tenant open-support-ticket count badge. */
  showOpenSupportBadge?: boolean;
}

/**
 * Data-driven tab array — adding a future tab is a one-line addition.
 * F133 adds the acquisition-cohorts tab as a separate tab (not a section
 * on the metrics page) — no layout collision with F130's metrics tile.
 * F162 adds the Suporte tab with an open-ticket count badge.
 */
const ADMIN_TABS: AdminTab[] = [
  { path: '', labelKey: 'admin.tabs.metrics', icon: BarChart3 },
  { path: 'acquisition', labelKey: 'admin.tabs.acquisition', icon: Megaphone },
  { path: 'support', labelKey: 'admin.tabs.support', icon: LifeBuoy, showOpenSupportBadge: true },
];

/**
 * F162 — cross-tenant `open + in_analysis` ticket count for the Suporte tab
 * badge. Fetched on mount and refreshed whenever `AdminSupportPage` dispatches
 * `ADMIN_SUPPORT_CHANGED_EVENT` after a mutation (mirrors the decoupled
 * `auth:unauthorized`/`admin:unauthorized` event convention — no polling,
 * no prop-drilling from a page nested two routes down).
 */
function useOpenSupportCount(): number | null {
  const [count, setCount] = useState<number | null>(null);

  const load = useCallback(() => {
    adminSupportService
      .getOpenCount()
      .then((result) => setCount(result.open_count))
      .catch(() => {
        // Silent — a failed badge fetch must never block the nav from rendering.
      });
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(ADMIN_SUPPORT_CHANGED_EVENT, load);
    return () => window.removeEventListener(ADMIN_SUPPORT_CHANGED_EVENT, load);
  }, [load]);

  return count;
}

function AdminMenu() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!admin) return null;

  function handleLogout() {
    logout();
    navigate(`/${ADMIN_SECRET}/login`, { replace: true });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-10 gap-2 px-2 hover:bg-muted"
          aria-label={t('admin.menu.account')}
        >
          <span className="text-sm font-medium">{admin.email}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {admin.email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="h-4 w-4 mr-2" />
          {t('admin.menu.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AdminLayout() {
  const { t } = useTranslation();
  const openSupportCount = useOpenSupportCount();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="h-14 border-b border-border flex items-center px-4 md:px-6 gap-4">
        <span className="text-sm font-semibold">Babilu · ops</span>
        <div className="ml-auto">
          <AdminMenu />
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row">
        {/*
         * Single data-driven nav — rendered once; CSS changes layout direction.
         * Mobile: horizontal scrollable strip below header.
         * Desktop: vertical sidebar column.
         * Rendering once ensures getByText() in tests finds exactly one element.
         */}
        <nav
          className="flex flex-row gap-1 p-2 overflow-x-auto border-b border-border md:flex-col md:w-56 md:border-b-0 md:border-r md:p-4 md:overflow-x-visible"
          aria-label="Admin navigation"
        >
          {ADMIN_TABS.map((tab) => {
            const Icon = tab.icon;
            const to = tab.path ? `/${ADMIN_SECRET}/${tab.path}` : `/${ADMIN_SECRET}`;
            const showBadge =
              tab.showOpenSupportBadge && openSupportCount !== null && openSupportCount > 0;
            return (
              <NavLink
                key={tab.labelKey}
                to={to}
                end
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors whitespace-nowrap',
                    'hover:bg-muted',
                    'min-h-11',
                    isActive && 'bg-muted font-medium'
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="truncate">{t(tab.labelKey)}</span>
                {showBadge ? (
                  <span
                    data-testid="admin-support-open-count-badge"
                    aria-label={t('admin.support.open_count_badge_aria', {
                      count: openSupportCount,
                    })}
                    className="ml-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full border border-warning/30 bg-warning/15 px-1.5 text-xs font-semibold text-warning-foreground"
                  >
                    {openSupportCount}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </nav>

        <main className="flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-7xl p-4 md:p-8 lg:p-12">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
